import { contentSealer } from '../crypto/sealContent';
import { openFolderName } from '../crypto/openContent';
import { t } from '../language';
import type { MeetingFolderDTO } from '../../shared/types';
import { getSupabase } from './client';
import { requireUserId } from './auth';

/**
 * Toplantı klasörleri (0059). Klasör adı başlık gibi şifrelenir; şifre bağlamı klasörün
 * kimliği olduğu için kimlik satır yazılmadan önce üretilir. Klasör silinince toplantılar
 * silinmez, klasörsüz kalır (`on delete set null`).
 */

const MAX_NAME = 120;

const cleanName = (name: unknown): string => {
  const value = typeof name === 'string' ? name.trim().slice(0, MAX_NAME) : '';
  if (!value) throw new Error(t('Klasör adı boş olamaz.', 'The folder name can’t be empty.'));
  return value;
};

export async function listFolders(): Promise<MeetingFolderDTO[]> {
  const userId = await requireUserId();
  const { data, error } = await getSupabase().from('meeting_folders').select('id, name, created_at').eq('user_id', userId).order('created_at');
  if (error) {
    console.error('[foldersRepo] listFolders:', error.message);
    throw new Error(t('Klasörler yüklenemedi.', 'Could not load folders.'));
  }
  const folders = await Promise.all(
    (data ?? []).map(async (row) => ({ id: row.id as string, name: await openFolderName(row.name as string, userId, row.id as string) })),
  );
  return folders.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
}

export async function createFolder(name: unknown): Promise<MeetingFolderDTO> {
  const clean = cleanName(name);
  const userId = await requireUserId();
  const id = crypto.randomUUID();
  const sealer = await contentSealer(userId, id);
  const { error } = await getSupabase()
    .from('meeting_folders')
    .insert({ id, user_id: userId, name: await sealer.field('meeting_folders.name', clean) });
  if (error) {
    console.error('[foldersRepo] createFolder:', error.message);
    throw new Error(t('Klasör oluşturulamadı.', 'Could not create the folder.'));
  }
  return { id, name: clean };
}

export async function renameFolder(id: string, name: unknown): Promise<void> {
  const clean = cleanName(name);
  const userId = await requireUserId();
  const sealer = await contentSealer(userId, id);
  const { error } = await getSupabase()
    .from('meeting_folders')
    .update({ name: await sealer.field('meeting_folders.name', clean) })
    .eq('id', id);
  if (error) {
    console.error('[foldersRepo] renameFolder:', error.message);
    throw new Error(t('Klasör adı değiştirilemedi.', 'Could not rename the folder.'));
  }
}

export async function deleteFolder(id: string): Promise<void> {
  const { error } = await getSupabase().from('meeting_folders').delete().eq('id', id);
  if (error) {
    console.error('[foldersRepo] deleteFolder:', error.message);
    throw new Error(t('Klasör silinemedi.', 'Could not delete the folder.'));
  }
}

/** `folderId` null ise toplantı klasörden çıkar. */
export async function moveMeetingToFolder(meetingId: string, folderId: string | null): Promise<void> {
  const { error } = await getSupabase().from('meetings').update({ folder_id: folderId }).eq('id', meetingId);
  if (error) {
    console.error('[foldersRepo] moveMeetingToFolder:', error.message);
    throw new Error(t('Toplantı klasöre taşınamadı.', 'Could not move the meeting to the folder.'));
  }
}
